import aa from 'search-insights';
import { globals } from './globals';
import { logger } from './logger';

export const getUserToken = async () =>
  new Promise((resolve, reject) => {
    aa('getUserToken', {}, (err, userToken) => {
      if (err) {
        reject(new Error(`Error fetching algolia anon user token: ${err}`)); // Reject the promise with an Error object
        return;
      }
      resolve(userToken); // Resolve the promise with the token
    });
  });

export const getAuthenticatedUserToken = async () =>
  new Promise((resolve, reject) => {
    aa('getAuthenticatedUserToken', {}, (err, authenticatedUserToken) => {
      if (err) {
        reject(new Error(`Error fetching algolia authenticated user token: ${err}`)); // Reject the promise with an Error object
        return;
      }
      resolve(authenticatedUserToken); // Resolve the promise with the token
    });
  });


export function updatePurchasedItems(itemCode: string, searchQueryID: string, objectData: { discount: string; price: string; quantity: number; }[] | undefined, total: string | undefined): void {
  if (!searchQueryID) {
    return;
  }
  const storedData = sessionStorage.getItem("purchasedObjectIDsAfterSearch");
  const purchasedItems = storedData ? JSON.parse(storedData) : [];

  purchasedItems.push({ itemCode, queryID: searchQueryID, objectData, total });

  sessionStorage.setItem("purchasedObjectIDsAfterSearch", JSON.stringify(purchasedItems));
}

export function trackPurchaseEvent(
  itemCode: string,
  queryID?: string
): void {
  const storageKey = "algoliaPurchasedItems";
  const maxEntries = 50;
  const storedData = sessionStorage.getItem(storageKey);
  const purchasedItems = storedData ? JSON.parse(storedData) : [];

  const purchaseItem: any = {
    itemCode
  };

  // Add queryID to data if it exists
  if (queryID) {
    purchaseItem.queryID = queryID;
  }

  purchasedItems.push(purchaseItem);

  // Keep only the most recent 50 entries
  if (purchasedItems.length > maxEntries) {
    purchasedItems.splice(0, purchasedItems.length - maxEntries);
  }

  sessionStorage.setItem(storageKey, JSON.stringify(purchasedItems));
}

export const getBasePrice = (skus: any, itemQty: number, isUnlimited: boolean): number => {
  const sku = skus?.[0];
  if (!sku) { return 0; }

  const { salePrice, buyersRRP, memberPrice } = sku;

  const basePrice = salePrice ?? buyersRRP;

  if (memberPrice && isUnlimited && itemQty >= 12) { return memberPrice }

  return basePrice;
};

export const getDiscount = (buyersRRP: number, price: number, itemQty: number): number =>
  (buyersRRP - price) * itemQty;

export const getTotal = (price: number, itemQty: number): number => (price * itemQty);

export const handleObjectData = (quantity: string, hit: any, isUnlimited: boolean) => {
  const itemQty = Number(quantity);

  // Get the skus from the hit
  const { skus } = hit;

  // Get the buyersRRP
  const buyersRRP = skus?.[0]?.buyersRRP;

  // Get the base price
  const price = getBasePrice(skus, itemQty, isUnlimited);

  // Calculate the discount
  const discount = getDiscount(buyersRRP, price, itemQty);

  // Calculate the total
  const total = getTotal(price, itemQty);

  logger.info('handleObjectData', {
    itemQty,
    buyersRRP,
    price,
    discount,
    total,
  });

  return { itemQty, discount: discount.toFixed(2), price: price.toFixed(2), total: total.toFixed(2) };
};

export const getQueryIDfromURL = (): string => {
  const url = new URL(window.location.href);
  const { searchParams } = url;

  return searchParams.get('queryID') ?? '';
};

export const handleAlgoliaCartEvent = async (
  itemCode: string,
  quantity: string,
  hit: any,
  isUnlimitedWithMember = false,
): Promise<void> => {
  const userToken = await getUserToken();
  const authenticatedUserToken = await getAuthenticatedUserToken();

  const index = globals.algoliaIndexName;
  const { itemQty, discount, price, total } = handleObjectData(quantity, hit, isUnlimitedWithMember);
  const currency = globals.country === 'us' ? 'USD' : 'GBP';

  const queryID = getQueryIDfromURL() || hit.__queryID;
  const objectData = [{ discount, price, quantity: itemQty }];

  const eventName = queryID ? 'addedToCartObjectIDsAfterSearch' : 'addedToCartObjectIDs';

  const aaPayload: Record<string, any> = {
    userToken,
    authenticatedUserToken,
    eventName,
    index,
    objectIDs: [itemCode],
    objectData,
    value: total,
    currency,
  };

  if (queryID) {
    aaPayload.queryID = queryID;
  }

  // Unified tracking function handles both search-driven and direct purchases
  trackPurchaseEvent(itemCode, queryID);

  aa(eventName, aaPayload);
};

